import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common';
import {
  CHECK_STOCK_ORDER_STATUSES,
  PROCESS_ORDER_STATUSES,
  OPEN_TRANSFER_STATUSES,
} from '../constants/internalOrdersConstants';
import {
  buildArticlesByUuid,
  formatOrderItemStatus,
  getArticleLabel,
  getInternalOrderApiErrorMessage,
  getProcessResultMessage,
} from '../utils/internalOrdersUtils';
import { useInventoryArticlesByUuids } from '../hooks/useInventoryArticlesByUuids';
import { useConsumptionsByOrder } from '../../../hooks/useConsumptions';
import { useWarehouses } from '../../../hooks/useWarehouses';
import GenerateConsumptionDialog from '../../consumptions/components/GenerateConsumptionDialog';
import {
  canGenerateConsumptionFromOrder,
  formatConsumptionStatus,
  getActiveConsumption,
} from '../../consumptions/utils/consumptionsUtils';

const InternalOrderDetailDialog = ({
  open,
  onClose,
  order,
  transfer,
  onCheckStock,
  onProcessOrder,
  onReceiveTransfer,
  onOrderUpdated,
  isProcessing = false,
  isLoading = false,
}) => {
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  const articleUuids = useMemo(
    () => (order?.items || []).map((item) => item.uuid_inventory_article),
    [order?.items]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && Boolean(order)
  );

  const {
    data: orderConsumptions = [],
    refetch: refetchOrderConsumptions,
  } = useConsumptionsByOrder(order?.uuid, {
    enabled: open && Boolean(order?.uuid),
  });

  const { data: warehouses = [] } = useWarehouses({
    enabled: generateDialogOpen,
  });

  useEffect(() => {
    if (open) {
      setResultMessage('');
      setErrorMessage('');
    }
  }, [open, order?.uuid]);

  const articlesByUuid = useMemo(
    () => buildArticlesByUuid(inventoryArticles),
    [inventoryArticles]
  );

  const canCheckStock =
    order && CHECK_STOCK_ORDER_STATUSES.includes(order.status);
  const canProcess =
    order && PROCESS_ORDER_STATUSES.includes(order.status) && !transfer;
  const canReceive =
    order &&
    transfer &&
    OPEN_TRANSFER_STATUSES.includes(transfer.status);
  const canGenerateConsumption = canGenerateConsumptionFromOrder(order, orderConsumptions);
  const activeConsumption = getActiveConsumption(orderConsumptions);

  const handleCheckStock = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const result = await onCheckStock(order.uuid);
      setResultMessage(getProcessResultMessage(result));
    } catch (error) {
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo controlar el stock'));
    }
  };

  const handleProcessOrder = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const result = await onProcessOrder(order.uuid);
      setResultMessage(getProcessResultMessage(result));
    } catch (error) {
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo procesar el pedido'));
    }
  };

  const handleGenerateSuccess = async () => {
    await refetchOrderConsumptions();
    await onOrderUpdated?.();
    setResultMessage('Consumo generado correctamente.');
  };

  if (!open) return null;

  if (isLoading || !order) {
    return (
      <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
        <DialogTitle>Detalle del pedido</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
      <DialogTitle>
        Pedido {order.number}
      </DialogTitle>

      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultMessage && <Alert severity="info">{resultMessage}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`Estado: ${order.status_label}`} color="primary" variant="outlined" />
            <Chip label={`Depósito: ${order.warehouse_label || '-'}`} variant="outlined" />
            <Chip label={`Proyecto: ${order.project_label || '-'}`} variant="outlined" />
            <Chip label={`Necesidad: ${order.requested_at_label || '-'}`} variant="outlined" />
          </Box>

          {order.observations && (
            <Typography variant="body2" color="text.secondary">
              Observaciones: {order.observations}
            </Typography>
          )}

          <Typography variant="subtitle1" fontWeight={700}>
            Materiales solicitados
          </Typography>

          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Artículo</HeadTableCell>
                  <HeadTableCell>Solicitado</HeadTableCell>
                  <HeadTableCell>Faltante</HeadTableCell>
                  <HeadTableCell>Estado</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(order.items || []).length === 0 ? (
                  <TableRow>
                    <RowTableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                      Sin materiales cargados
                    </RowTableCell>
                  </TableRow>
                ) : (
                  (order.items || []).map((item) => (
                    <TableRow key={item.uuid}>
                      <RowTableCell>
                        {getArticleLabel(articlesByUuid, item.uuid_inventory_article)}
                      </RowTableCell>
                      <RowTableCell>{item.requested_quantity}</RowTableCell>
                      <RowTableCell>{item.missing_quantity}</RowTableCell>
                      <RowTableCell>{formatOrderItemStatus(item.status)}</RowTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>

          {transfer && (
            <Alert severity="warning">
              Traslado {transfer.transfer_number} — estado: {transfer.status}
            </Alert>
          )}

          {activeConsumption && (
            <Alert severity="info">
              Consumo activo: {activeConsumption.consumption_number} (
              {formatConsumptionStatus(activeConsumption.status)})
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {canCheckStock && (
              <Button
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={handleCheckStock}
                disabled={isProcessing}
              >
                Controlar stock
              </Button>
            )}
            {canProcess && (
              <Button
                variant="contained"
                startIcon={<LocalShippingIcon />}
                onClick={handleProcessOrder}
                disabled={isProcessing}
              >
                Procesar pedido
              </Button>
            )}
            {canReceive && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<LocalShippingIcon />}
                onClick={() => onReceiveTransfer(transfer)}
                disabled={isProcessing}
              >
                Recibir traslado
              </Button>
            )}
            {canGenerateConsumption && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ShoppingCartCheckoutIcon />}
                onClick={() => setGenerateDialogOpen(true)}
                disabled={isProcessing}
              >
                Generar consumo
              </Button>
            )}
          </Box>
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={() => {}}
        saveButtonProps={{ sx: { display: 'none' } }}
        cancelLabel="Cerrar"
      />

      <GenerateConsumptionDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        order={order}
        warehouses={warehouses}
        defaultWarehouseId={order?.uuid_destination_warehouse}
        onSuccess={handleGenerateSuccess}
      />
    </DialogContainer>
  );
};

export default InternalOrderDetailDialog;
